/* Markets Page — Live market data: indices, options, sectors, commodities, currency
   Uses Phase 4.1 components with tab switching */

import { renderIndexCards } from '../components/IndexCards';
import { renderOptionsChain } from '../components/OptionsChain';
import { renderSectorHeatmap } from '../components/SectorHeatmap';
import { renderCommodityPanel } from '../components/CommodityPanel';
import { renderCurrencyPanel } from '../components/CurrencyPanel';
import { renderMarketBreadthViz } from '../components/MarketBreadthViz';
import { renderPhase2BreadthEngine } from '../components/charts/Phase2BreadthEngine';
import { renderPhase2IndicesEngine } from '../components/charts/Phase2IndicesEngine';
import { renderPhase2OptionsMicrostructure } from '../components/charts/Phase2OptionsMicrostructure';
import { renderPhase2SectorsEngine } from '../components/charts/Phase2SectorsEngine';
import { isFeatureEnabled } from '../services/runtime-config';

interface MarketTab {
  id: string;
  label: string;
  render: (el: HTMLElement) => void;
}

type CommandEventDetail = {
  symbol?: string;
};

const TABS: MarketTab[] = [
  {
    id: 'indices',
    label: 'Indices',
    render: (el: HTMLElement) => {
      if (isFeatureEnabled('charts_v2_engine')) {
        renderPhase2IndicesEngine(el);
        return;
      }
      renderIndexCards(el);
    },
  },
  {
    id: 'options',
    label: 'Options',
    render: (el: HTMLElement) => {
      if (isFeatureEnabled('charts_v2_engine')) {
        renderPhase2OptionsMicrostructure(el);
        return;
      }
      renderOptionsChain(el);
    },
  },
  {
    id: 'sectors',
    label: 'Sectors',
    render: (el: HTMLElement) => {
      if (isFeatureEnabled('charts_v2_engine')) {
        renderPhase2SectorsEngine(el);
        return;
      }
      renderSectorHeatmap(el);
    },
  },
  { id: 'commodities', label: 'Commodities', render: renderCommodityPanel },
  { id: 'currency', label: 'Currency', render: renderCurrencyPanel },
  {
    id: 'breadth',
    label: 'Breadth',
    render: (el: HTMLElement) => {
      if (isFeatureEnabled('charts_v2_engine')) {
        renderPhase2BreadthEngine(el);
        return;
      }
      renderMarketBreadthViz(el);
    },
  },
];

let detachCommandHandlers: (() => void) | null = null;

export function renderMarkets(): string {
  const tabButtons = TABS.map((t, i) =>
    `<button class="period-toggle__btn ${i === 0 ? 'period-toggle__btn--active' : ''}" data-market-tab="${t.id}">${t.label}</button>`
  ).join('');

  return `
    <div id="market-command-context" class="text-xs text-muted" style="margin-bottom: var(--space-3);"></div>
    <div style="margin-bottom: var(--space-4);">
      <div class="period-toggle" id="market-tabs" style="margin-bottom: var(--space-6);">
        ${tabButtons}
      </div>
    </div>
    <div id="market-content"></div>
  `;
}

export function initMarkets(): void {
  const contentEl = document.getElementById('market-content');
  const tabsEl = document.getElementById('market-tabs');
  const contextEl = document.getElementById('market-command-context');
  if (!contentEl || !tabsEl) return;

  const setContext = (value: string): void => {
    if (!contextEl) return;
    contextEl.textContent = value;
  };

  const switchTab = (tabId: string): void => {
    const tab = TABS.find(t => t.id === tabId);
    if (!tab) return;

    tabsEl.querySelectorAll('.period-toggle__btn').forEach(b => b.classList.remove('period-toggle__btn--active'));
    const activeBtn = tabsEl.querySelector(`[data-market-tab="${tabId}"]`);
    activeBtn?.classList.add('period-toggle__btn--active');

    tab.render(contentEl);
  };

  // Render first tab
  switchTab(TABS[0].id);

  // Tab switching
  tabsEl.querySelectorAll('[data-market-tab]').forEach(btn => {
    btn.addEventListener('click', () => {
      const tabId = (btn as HTMLElement).dataset.marketTab;
      if (!tabId) return;
      switchTab(tabId);
    });
  });

  if (detachCommandHandlers) {
    detachCommandHandlers();
  }

  const onBreadth = () => {
    setContext('Command: BREADTH');
    switchTab('breadth');
  };

  const onChain = () => {
    setContext('Command: CHAIN');
    switchTab('options');
  };

  const onSymbol = (event: Event) => {
    const detail = (event as CustomEvent<CommandEventDetail>).detail;
    const symbol = detail?.symbol?.toUpperCase() || '';
    if (!symbol) return;
    setContext(`Command: SYMBOL ${symbol} GO`);
    switchTab('options');
    document.dispatchEvent(new CustomEvent('imm:options:symbol', { detail: { symbol } }));
  };

  document.addEventListener('imm:command:breadth', onBreadth);
  document.addEventListener('imm:command:chain', onChain);
  document.addEventListener('imm:command:symbol', onSymbol as EventListener);

  detachCommandHandlers = () => {
    document.removeEventListener('imm:command:breadth', onBreadth);
    document.removeEventListener('imm:command:chain', onChain);
    document.removeEventListener('imm:command:symbol', onSymbol as EventListener);
  };
}
